import { Segment } from '@dna/analytics'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import { rothysSignetBadge } from '@dna/paths'
import styles from '@dna/styles'
import { handleize, t } from '@dna/utils'
import { LitElement, css, html, nothing, repeat, when } from '@lit'

class DNAStoreList extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    locations: {
      type: Array,
    },
    isInfoWindow: {
      type: Boolean,
    },
  }

  get locationsCount() {
    return this.locations.length
  }

  constructor() {
    super()

    this._setMobileBreakpoints(['mobile'])
    this.isInfoWindow = false
  }

  async connectedCallback() {
    super.connectedCallback()

    this.hasSearched = false
    this.hasLocation = false
    this.hasResults = false
    if (!this.locations) this.locations = []

    document.addEventListener(
      'dna:store-locator:list-update',
      this._updateStoreList,
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.removeEventListener(
      'dna:store-locator:list-update',
      this._updateStoreList,
    )
  }

  _updateStoreList = (event) => {
    if (event.detail.stores.length) {
      this.hasLocation = true
      this.hasResults = true
      this.locations = event.detail.stores.sort(
        (a, b) => a.distance_from_user - b.distance_from_user,
      )
      this.classList.add('active')
    } else {
      this.hasLocation = false
      this.hasResults = false
      this.locations = []
      this.classList.add('active')
    }

    this.hasSearched = true
    if (event.detail.status === 'search cleared') {
      this.classList.remove('active')
      this.hasSearched = false
    }
  }

  _handleStoreLinkClick = (event, location) => {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.STORE_CLICKED, {
        store_name: location.fields.name,
        store_url: location.onlineStoreUrl,
        stores_context: 'search results',
      })
    })
  }

  _renderStoreInList(store) {
    return html`
      <div class="store ${this.isInfoWindow ? 'store__info-window' : ''}">
        <div class="store__header flex flex-space-between">
          <h3 class="store__name h6 mt-2">
            ${when(
              store.type === 'wholesale_stores',
              () => store.fields.name,
              () => html`
                <a
                  href="${store.onlineStoreUrl}"
                  @click=${(e) => this._handleStoreLinkClick(e, store)}
                >
                  ${store.fields.name}
              </a>`,
            )}
          </h3>
          ${when(
            store.type !== 'wholesale_stores' && !this.isInfoWindow,
            () => html`
              <span class="store__icon flex flex-align-center flex-justify-center">
                ${rothysSignetBadge}
              </span>
            `,
            () => nothing,
          )}
        </div>
        <div class="store__details flex flex-column">
          ${when(
            store.fields.hours_api_json,
            () => html`
              <p class="store__details-hours p2">
                ${when(
                  store.open_now,
                  () => html`
                    <span class="c-reflex-blue uppercase label-m">
                      ${t('templates.stores.locator.open_now')}
                    </span>
                    ${when(
                      store.open_between,
                      () => html`
                        <span class="c-disabled"> ${t('templates.stores.locator.today')} ${store.open_between} ${this.hasLocation ? `(${store.distance_from_user}mi)` : ''}</span>
                      `,
                      () => nothing,
                    )}
                  `,
                  () => html`
                    <span class="c-reflex-blue uppercase label-m">
                      ${t('templates.stores.locator.closed_now')}
                    </span>
                    ${when(
                      store.open_next,
                      () => html`
                        <span class="c-disabled"> ${t('templates.stores.locator.until')} ${store.open_next} </span>
                      `,
                      () => nothing,
                    )}
                  `,
                )}
              </p>
            `,
          )}
          <div class="store__details-address">
            <p class="p2">
              ${store.fields.street_address}<br/>
              ${store.fields.city}, ${store.fields.state} ${store.fields.zip}
            </p>
          </div>
          <div class="store__details-contact">
            ${when(
              store.type === 'wholesale_stores',
              () => html`
                <div class="flex wrap flex-space-between p2">
                  <a href=${store.fields.google_maps_url}>
                    ${t('templates.stores.locator.directions')}
                  </a>
                  ${when(
                    store.fields.contact_phone,
                    () => html`
                      <p class="p2 store__details-phone">
                        <a href="tel:${store.fields.contact_phone
                          .replaceAll('-', '')
                          .replaceAll('(', '')
                          .replaceAll(')', '')
                          .replaceAll(' ', '')
                          .trim()}"
                        >
                          ${store.fields.contact_phone}
                        </a>
                      </p>
                    `,
                    () => nothing,
                  )}
                </div>
              `,
              () => html`
                <div class="flex wrap flex-space-between">
                  ${when(
                    store.fields.contact_email,
                    () => html`
                      <p class="p2">
                        <a href="mailto:${store.fields.contact_email}">${store.fields.contact_email}</a>
                      </p>
                    `,
                    () => nothing,
                  )}
                  ${when(
                    store.fields.contact_phone,
                    () => html`
                      <p class="p2 store__details-phone">
                        <a href="tel:${store.fields.contact_phone
                          .replaceAll('-', '')
                          .replaceAll('(', '')
                          .replaceAll(')', '')
                          .replaceAll(' ', '')
                          .trim()}"
                        >
                          ${store.fields.contact_phone}
                        </a>
                      </p>
                    `,
                    () => nothing,
                  )}
                </div>
                ${when(
                  store.fields.google_maps_url,
                  () => html`
                    <p class="p2">
                      <a href=${store.fields.google_maps_url}>
                        ${t('templates.stores.locator.directions')}
                      </a>
                    </p>
                  `,
                )}
              `,
            )}
          </div>
        </div>
      </div>
    `
  }

  _renderNoResults() {
    const $noResults = this.querySelector('.js-no-store-results')

    return html`
      ${when(
        this.hasSearched && !this.hasResults,
        () => {
          $noResults?.classList.remove('hidden')
          return html`
            <slot name="no-results"></slot>
          `
        },
        () => nothing,
      )}
    `
  }

  render() {
    return html`
      ${this._renderNoResults()}
      ${when(
        this.locationsCount > 0,
        () =>
          repeat(
            this.locations,
            (location) => handleize(location.fields.name),
            (location) => this._renderStoreInList(location),
          ),
        () => nothing,
      )}
    `
  }

  static styles = [
    styles.a11y,
    styles.color,
    styles.container,
    styles.flex,
    styles.spacing,
    styles.typography,
    css`
      :host {
        display: block;
        overflow-y: auto;
      }
      ::slotted(.store-list__no-results) {
        padding: var(--dna-space-6) var(--dna-space-4);
      }
      .store {
        border: 1px solid var(--dna-color-light-grey);
        margin-bottom: var(--dna-space-2);
        padding: var(--dna-space-2) var(--dna-space-4) var(--dna-space-1);

        .store__distance {
          margin-top: var(--dna-space-2);
        }
        .store__name {
          margin-top: 0;
          margin-right: var(--dna-space-3);
          margin-bottom: var(--dna-space-1);
        }
        .store__icon {
          background-color: var(--dna-color-reflex-blue);
          border-radius: 100%;
          height: calc(var(--dna-space-6) + var(--dna-space-1));
          width: calc(var(--dna-space-6) + var(--dna-space-1));
          position: relative;
          top: var(--dna-space-0-5);
          left: var(--dna-space-1);
        }
        p {
          margin: 0;
          margin-bottom: var(--dna-space-2);
        }
        a {
          color: var(--dna-brand-primary);
          text-decoration: underline;
        }
        .store__details {
          gap: var(--dna-space-2);

          .store__details-contact p {
            flex: 0 0 auto;
          }
        }
        .store__details-hours {
          margin-bottom: 0;
        }
        .store__details-address p {
          margin-bottom: 0;
        }
      }
      .store__info-window {
        border: none;
        padding: 0px;
      }
      .store__info-window .store__name {
        margin-right: var(--dna-space-5);
      }
      @media screen and (min-width: 1024px) {
        .store-list__no-results {
          margin-top: var(--dna-space-6);
          padding: var(--dna-space-6);
        }
        .store__info-window .store__details-phone {
          margin-left: var(--dna-space-2);
        }
      }
      @media screen and (min-width: 1200px) {
        .store-list__no-results {
          padding: calc(var(--dna-space-10) + var(--dna-space-4)) var(--dna-space-10);
        }
      }
    `,
  ]
}
customElements.define('dna-store-list', DNAStoreList)
